<template>
    <div class="image-component-root">
        <div id="prescription-image-component" v-if="(imageID || escriptID)" class="h-100">
            <div v-if="loadingImage" class="text-center m-4">
                <b-spinner small /> Loading Image...
            </div>

            <!-- Showing PDF -->
            <iframe v-if="pdfSrc && !loadingImage" :src="pdfSrc"
                style="height: 100%; width: 100%; border: none;" />

            <!-- File not found -->
            <div v-if="!imageSrc && !pdfSrc && !loadingImage" class="no-image h-100">
                <h3 class="m-4 text-center font-weight-bold text-muted">
                    <i class="fa fa-warning fa-sm" />
                    File not found
                </h3>
            </div>

            <!-- IMAGE FOUND -->
            <Viewer ref="viewer" v-if="imageSrc && !loadingImage" class="viewer" :options="viewerOptions()" :images="[imageSrc]">
                <template #default="scope">
                    <img v-for="src in scope.images" :src="src" :key="src" :alt="src" class="d-none">
                </template>
            </Viewer>
        </div>
        <!-- No image has been loaded. -->
        <div v-else-if="!rxNumber">
            <div class="no-image">
                <h4 class="m-4 text-center font-weight-bold text-muted">
                    <i class="fa fa-warning fa-sm" />
                    No RX Loaded
                </h4>
            </div>
        </div>
        <div v-else>
            <div class="no-image">
                <h4 class="m-4 text-center font-weight-bold text-muted">
                    <i class="fa fa-warning fa-sm" />
                    No image loaded in this prescription.
                </h4>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import { component as Viewer } from 'v-viewer';

@Component({
    name: "ImageComponent",
    components: {
        Viewer
    }
})
export default class ImageComponent extends Vue {
    @Prop() private imageID!: number;
    @Prop() private escriptID!: number;
    @Prop() protected rxNumber!: number;

    protected imageSrc?: any = null;
    protected pdfSrc: string | null = null;

    private imageRequest?: any = null;
    private loadingImage = false;

    // Monotonic token identifying the newest fetch. A superseded fetch unwinds through its own
    // `finally` after being cancelled, and without this it would clear the request handle and
    // spinner belonging to the fetch that replaced it.
    private imageSequence = 0;

    viewerOptions(): Viewer.Options {
        return {
            inline: true,
            button: false,
            title: false,
            transition: false,
            navbar: true,
            focus: false,
            toolbar: {
                zoomIn: 1,
                zoomOut: 1,
                reset: 1,
                prev: 1,
                next: 1,
                rotateLeft: 1,
                rotateRight: 1,
                flipHorizontal: 1,
                flipVertical: 1,
                oneToOne: 0,
                play: 0
            }
        };
    }

    created() {
        console.log("%c Created ImageComponent", "color: green;");
        if (this.imageID) {
            this.fetchPrescriptionImageSource(`image/${this.imageID}`);
        } else if (this.escriptID) {
            this.fetchPrescriptionImageSource(`escript/${this.escriptID}/image`);
        }
    }

    beforeDestroy() {
        console.log('%c Destroying ImageComponent', 'color: red');

        // Retire the sequence and drop the in-flight request. A response that lands after teardown
        // would otherwise mint an object URL with nothing left to revoke it.
        this.imageSequence++;
        this.imageRequest?.cancel();

        const viewer = this.$refs["viewer"] as any;
        if (viewer && viewer.destroy) {
            viewer.destroy();
        }

        this.releaseObjectUrls();
    }

    @Watch('imageID') imageIDUpdated(value: number) {
        if (value) {
            this.fetchPrescriptionImageSource(`image/${value}`);
        }
    }

    // The escript image endpoint keys off the escript ID directly, so it is requested straight from
    // the prop. Reading the ID off the escript detail endpoint first only returned the ID we already
    // had, at the cost of a second round-trip that re-reads the same image from S3.
    @Watch('escriptID') escriptIDUpdated(value: number) {
        if (value) {
            this.fetchPrescriptionImageSource(`escript/${value}/image`);
        }
    }

    // Both sources hold object URLs; releasing both stops an image view stranding a blob for the
    // lifetime of the tab.
    private releaseObjectUrls() {
        if (this.imageSrc) {
            URL.revokeObjectURL(this.imageSrc);
        }
        if (this.pdfSrc) {
            URL.revokeObjectURL(this.pdfSrc);
        }

        this.imageSrc = null;
        this.pdfSrc = null;
    }

    async fetchPrescriptionImageSource(imageUrl?: string) {
        if (!imageUrl) return;

        const requestId = ++this.imageSequence;

        try {
            this.loadingImage = true;

            // Explicitly destroy viewer before switching to PDF
            const viewer = this.$refs["viewer"] as any;
            if (viewer && viewer.$viewer) {
                viewer.$viewer.destroy();
            }

            this.releaseObjectUrls();

            if (this.imageRequest) {
                this.imageRequest.cancel();
            }

            const axiosSource = Axios.CancelToken.source();

            this.imageRequest = { cancel: axiosSource.cancel };

            // The endpoint returns the file inline, so one response carries both bytes and content type.
            const imageDataResponse = await Axios.get(imageUrl, {
                responseType: "blob",
                cancelToken: axiosSource.token
            });

            if (requestId !== this.imageSequence) return;

            const objectUrl = URL.createObjectURL(imageDataResponse.data);

            // A content type may carry parameters, so compare the media type rather than the header.
            const contentType: string = imageDataResponse.headers["content-type"] || "";
            const isPdf = contentType.split(";")[0].trim().toLowerCase() === "application/pdf";

            if (!isPdf) {
                this.imageSrc = objectUrl;
                this.$nextTick(() => {
                    const viewer = this.$refs["viewer"] as any;
                    if (viewer) viewer.rebuildViewer();
                });
                return;
            }

            this.pdfSrc = objectUrl;
        } catch (err) {
            // Cancellation is the designed outcome of a rapid prescription switch or a teardown
            // mid-load, not a fault; logging it would report an error for working behavior.
            if (!Axios.isCancel(err)) {
                console.error(err);
            }
        }
        finally {
            if (requestId === this.imageSequence) {
                this.imageRequest = null;
                this.loadingImage = false;
            }
        }
    }
}
</script>

<style scoped>
.image-component-root {
    height: 100%;
}

#prescription-image-component {
    resize: both;
    z-index: 100;
}
</style>
